import { SerializedError } from "@reduxjs/toolkit";
import { FetchBaseQueryError } from "@reduxjs/toolkit/query";
import { Check, Loader2, X } from "lucide-react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { CustomToolTip } from "../CustomToolTip";

import PaginatedLoader from "./component/PaginatedLoader";

import { Badge } from "@/components/ui/badge";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import { Side } from "@/components/ui/customChervonIcon";
import {
  Popover,
  PopoverContent,
  PopoverTriggerButton,
} from "@/components/ui/popover";
import useDebounce from "@/hooks/useDebounce";
import { LabelValue } from "@/interfaces/interfaces";
import { cn } from "@/lib/utils";

export interface Option {
  values: string;
  label: string;
  disabled?: boolean;
  description?: string;
  icon?: React.ReactNode;
}

export interface RTKQueryHookResult<T> {
  data?: GeneralResponse<T[]>;
  error?: FetchBaseQueryError | SerializedError;
  isLoading: boolean;
  isFetching: boolean;
  refetch: () => void;
}

export interface RTKQueryHook<T, P> {
  (params: P, options?: UseQueryOptions): RTKQueryHookResult<T>;
}

export interface PaginationState {
  offset: number;
  limit: number;
  query?: string;
}

export interface FilterConfig {
  offsetKey?: string;
  limitKey?: string;
  queryKey?: string;
  queryParamsParamKey?: string;
  customBuilder?: (params: PaginationState) => string;
}

export type RenderConfig<T> = {
  valueKey: keyof T;
  labelKey?: keyof T;
  labelRenderer?: (option: T) => React.ReactNode;
};

type PaginatedQueryResult<T, P> = ReturnType<RTKQueryHook<T, P>> & {
  paginationState: PaginationState;
  setPaginationState: React.Dispatch<React.SetStateAction<PaginationState>>;
  setOffset: (offset: number) => void;
  setLimit: (limit: number) => void;
  setQuery: (query?: string) => void;
  resetPagination: () => void;
  loadMore: () => void;
  hasMore: boolean;
};

const usePaginatedQuery = <T, P = void>(
  useQuery: RTKQueryHook<T, P>,
  baseParams?: P extends void ? never : Omit<P, "filter">,
  config: FilterConfig = {},
  skip: boolean = false,
): PaginatedQueryResult<T, P> => {
  const [paginationState, setPaginationState] = useState<PaginationState>({
    offset: 0,
    limit: 10,
  });

  const {
    offsetKey = "offset",
    limitKey = "limit",
    queryKey = "search",
    queryParamsParamKey = "filter",
    customBuilder,
  } = config;

  const filterString = useMemo(() => {
    if (customBuilder) {
      return customBuilder(paginationState);
    }

    const params = new URLSearchParams();
    params.append(offsetKey, paginationState.offset.toString());
    params.append(limitKey, paginationState.limit.toString());
    if (paginationState.query) {
      params.append(queryKey, paginationState.query);
    }

    const hasBaseParams =
      baseParams && Object.keys(baseParams as object).length > 0;
    const prefix = hasBaseParams ? "&" : "?";

    return `${prefix}${params.toString()}`;
  }, [
    paginationState,
    offsetKey,
    limitKey,
    queryKey,
    customBuilder,
    baseParams,
  ]);

  const queryParams = useMemo(
    () =>
      ({
        ...baseParams,
        [queryParamsParamKey]: filterString,
      }) as P,
    [baseParams, filterString, queryParamsParamKey],
  );

  const getQueries = useMemo((): P => {
    if (baseParams) {
      return queryParams as P;
    }
    if (typeof baseParams === "string") {
      return filterString as P;
    }

    return {
      [queryParamsParamKey]: filterString,
    } as P;
  }, [baseParams, queryParams, filterString, queryParamsParamKey]);

  const queryResult = useQuery(getQueries, {
    skip,
  });

  const setOffset = useCallback((offset: number) => {
    setPaginationState((prev) => ({ ...prev, offset }));
  }, []);

  const setLimit = useCallback((limit: number) => {
    setPaginationState((prev) => ({ ...prev, limit, offset: 0 }));
  }, []);

  const setQuery = useCallback((query?: string) => {
    setPaginationState((prev) => ({ ...prev, query, offset: 0 }));
  }, []);

  const resetPagination = useCallback(() => {
    setPaginationState((prev) => ({ ...prev, offset: 0 }));
  }, []);

  const loadMore = useCallback(() => {
    setPaginationState((prev) => ({
      ...prev,
      offset: prev.offset + prev.limit,
    }));
  }, []);

  return {
    ...queryResult,
    paginationState,
    setPaginationState,
    setOffset,
    setLimit,
    setQuery,
    resetPagination,
    loadMore,
    hasMore: queryResult.data?.pagination?.totalResult
      ? paginationState.offset + (queryResult.data?.data?.length || 0) <
        queryResult.data.pagination.totalResult
      : (queryResult.data?.data?.length || 0) >= paginationState.limit,
  };
};

export interface AsyncMultiSelectProps<T, P = void> {
  useQuery: RTKQueryHook<T, P>;
  baseParams?: P extends void ? never : Omit<P, "filter">;
  filterConfig?: FilterConfig;
  preload?: boolean;
  filterFn?: (option: T, query: string) => boolean;
  renderConfig: RenderConfig<T>;
  defaultLabel?: string;
  defaultLabels?: LabelValue[];
  notFound?: React.ReactNode;
  loadingSkeleton?: React.ReactNode;
  values: string[];
  onChange: (values: string[], objects?: T[]) => void;
  label: string;
  placeholder?: string;
  disabled?: boolean;
  width?: string | number;
  className?: string;
  triggerClassName?: string;
  noResultsMessage?: string;
  clearable?: boolean;
  limit?: number;
  loadMoreThreshold?: number;
  side?: Side;
  maxSelectedDisplay?: number;
  allowSelectAll?: boolean;
}

export const AsyncMultiSelect = <T, P = void>({
  useQuery,
  baseParams,
  filterConfig,
  preload,
  filterFn,
  renderConfig,
  defaultLabel,
  defaultLabels,
  notFound,
  loadingSkeleton,
  label,
  placeholder = "Select...",
  values = [],
  onChange,
  disabled = false,
  className,
  triggerClassName,
  noResultsMessage,
  clearable = true,
  limit = 10,
  loadMoreThreshold = 100,
  side,
  maxSelectedDisplay = 3,
  allowSelectAll = false,
}: AsyncMultiSelectProps<T, P>): JSX.Element => {
  const [mounted, setMounted] = useState(false);
  const [open, setOpen] = useState(false);
  const [allOptions, setAllOptions] = useState<T[]>([]);
  const [displayOptions, setDisplayOptions] = useState<T[]>([]);
  const [selectedValues, setSelectedValues] = useState<string[]>(values);
  const [selectedOptions, setSelectedOptions] = useState<T[]>([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [isLoadingMore, setIsLoadingMore] = useState(false);

  const [selectedOptionsCache, setSelectedOptionsCache] = useState<
    Map<string, T>
  >(new Map());

  const prevOptionsRef = useRef<T[]>([]);
  const prevOffsetRef = useRef<number>(0);

  const debouncedSearchTerm = useDebounce(searchTerm, preload ? 0 : 300);
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  const skipQuery = !open && !preload;

  const {
    data,
    error,
    isLoading,
    isFetching,
    paginationState,
    setLimit,
    setQuery,
    loadMore,
    hasMore,
  } = usePaginatedQuery(
    useQuery,
    baseParams,
    {
      ...filterConfig,
      limitKey: filterConfig?.limitKey || "limit",
    },
    skipQuery,
  );

  const getOptionValue = useMemo(() => {
    return (option: T): string => {
      const values = option[renderConfig.valueKey];

      return String(values || "");
    };
  }, [renderConfig.valueKey]);

  const renderOption = useCallback(
    (option: T): React.ReactNode => {
      if (renderConfig.labelRenderer) {
        return renderConfig.labelRenderer(option);
      }

      if (renderConfig.labelKey) {
        const label = option[renderConfig.labelKey];

        return <div>{String(label || "")}</div>;
      }

      const values = option[renderConfig.valueKey];

      return <div>{String(values || "")}</div>;
    },
    [renderConfig],
  );

  const getDisplayValue = useCallback(
    (option: T): React.ReactNode => {
      const displayKey = renderConfig.labelKey || renderConfig.valueKey;
      const displayValue = option[displayKey];

      if (renderConfig.labelRenderer) {
        return renderConfig.labelRenderer(option);
      }

      return String(displayValue || "");
    },
    [renderConfig],
  );

  useEffect(() => {
    setMounted(true);
  }, []);

  useEffect(() => {
    if (!mounted) {
      return;
    }

    const valuesChanged =
      values.length !== selectedValues.length ||
      values.some((val, index) => val !== selectedValues[index]);

    if (valuesChanged) {
      setSelectedValues(values);
    }
  }, [values, mounted]);

  useEffect(() => {
    if (paginationState.limit !== limit) {
      setLimit(limit);
    }
  }, [limit, paginationState.limit, setLimit]);

  const setIsLoadingMoreCallback = useCallback((loading: boolean) => {
    setIsLoadingMore(loading);
  }, []);

  useEffect(() => {
    if (mounted && !skipQuery) {
      setQuery(debouncedSearchTerm || undefined);
      setIsLoadingMoreCallback(false);
    }
  }, [
    debouncedSearchTerm,
    setQuery,
    mounted,
    skipQuery,
    setIsLoadingMoreCallback,
  ]);

  const options = data?.data || [];

  useEffect(() => {
    if (!skipQuery && options.length > 0) {
      const optionsChanged =
        options.length !== prevOptionsRef.current.length ||
        options.some(
          (opt, index) =>
            getOptionValue(opt) !==
            getOptionValue(prevOptionsRef.current[index] || ({} as T)),
        );

      const offsetChanged = paginationState.offset !== prevOffsetRef.current;

      if (optionsChanged || offsetChanged) {
        setSelectedOptionsCache((prevCache) => {
          const newCache = new Map(prevCache);
          let cacheUpdated = false;

          options.forEach((option) => {
            const optionValue = getOptionValue(option);
            if (
              optionValue &&
              (selectedValues.includes(optionValue) ||
                prevCache.has(optionValue)) &&
              !newCache.has(optionValue)
            ) {
              newCache.set(optionValue, option);
              cacheUpdated = true;
            }
          });

          return cacheUpdated ? newCache : prevCache;
        });

        if (paginationState.offset === 0) {
          setAllOptions(options);
        } else {
          setAllOptions((prev) => {
            const newOptions = options.filter(
              (newOpt) =>
                !prev.some(
                  (existingOpt) =>
                    getOptionValue(existingOpt) === getOptionValue(newOpt),
                ),
            );

            return [...prev, ...newOptions];
          });
        }

        prevOptionsRef.current = options;
        prevOffsetRef.current = paginationState.offset;
      }

      setIsLoadingMore(false);
    } else if (!skipQuery && paginationState.offset === 0) {
      setAllOptions([]);
      prevOptionsRef.current = [];
      prevOffsetRef.current = 0;
    }
  }, [
    options,
    paginationState.offset,
    getOptionValue,
    skipQuery,
    values,
    selectedValues,
  ]);

  useEffect(() => {
    let filteredOptions = allOptions;

    if (preload && debouncedSearchTerm && filterFn) {
      filteredOptions = allOptions.filter((option) =>
        filterFn(option, debouncedSearchTerm),
      );
    }

    const optionValues = new Set(filteredOptions.map(getOptionValue));
    const additionalOptions: T[] = [];

    selectedOptionsCache.forEach((cachedOption, cachedValue) => {
      if (!optionValues.has(cachedValue)) {
        if (preload && debouncedSearchTerm && filterFn) {
          if (filterFn(cachedOption, debouncedSearchTerm)) {
            additionalOptions.push(cachedOption);
          }
        } else if (!debouncedSearchTerm || preload) {
          additionalOptions.push(cachedOption);
        }
      }
    });

    setDisplayOptions([...filteredOptions, ...additionalOptions]);
  }, [
    preload,
    allOptions,
    debouncedSearchTerm,
    filterFn,
    selectedOptionsCache,
    getOptionValue,
  ]);

  useEffect(() => {
    if (selectedValues.length > 0) {
      const options: T[] = [];

      selectedValues.forEach((val) => {
        let option = displayOptions.find((opt) => getOptionValue(opt) === val);

        if (!option && selectedOptionsCache.has(val)) {
          option = selectedOptionsCache.get(val);
        }

        if (option) {
          options.push(option);
        }
      });

      setSelectedOptions(options);
    } else {
      setSelectedOptions([]);
    }
  }, [selectedValues, displayOptions, getOptionValue, selectedOptionsCache]);

  const handleScroll = useCallback(
    (e: React.UIEvent<HTMLDivElement>) => {
      if (preload || !hasMore || isFetching || isLoading || isLoadingMore) {
        return;
      }

      const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;
      const isNearBottom =
        scrollHeight - scrollTop - clientHeight < loadMoreThreshold;

      if (isNearBottom) {
        setIsLoadingMore(true);
        loadMore();
      }
    },
    [
      preload,
      hasMore,
      isFetching,
      isLoading,
      isLoadingMore,
      loadMoreThreshold,
      loadMore,
    ],
  );

  const handleSelect = useCallback(
    (currentValue: string) => {
      const isSelected = selectedValues.includes(currentValue);
      let newValues: string[];

      if (isSelected) {
        newValues = selectedValues.filter((val) => val !== currentValue);
      } else {
        newValues = [...selectedValues, currentValue];
      }

      setSelectedValues(newValues);

      // Update cache with newly selected options
      const option = displayOptions.find(
        (opt) => getOptionValue(opt) === currentValue,
      );

      if (option && !isSelected) {
        const newCache = new Map(selectedOptionsCache);
        newCache.set(currentValue, option);
        setSelectedOptionsCache(newCache);
      }

      // Get all selected option objects
      const selectedObjects = newValues
        .map((val) => {
          return (
            displayOptions.find((opt) => getOptionValue(opt) === val) ||
            selectedOptionsCache.get(val)
          );
        })
        .filter(Boolean) as T[];

      onChange(newValues, selectedObjects);
    },
    [
      selectedValues,
      onChange,
      displayOptions,
      getOptionValue,
      selectedOptionsCache,
    ],
  );

  const handleSelectAll = useCallback(() => {
    if (selectedValues.length === displayOptions.length) {
      // Deselect all
      setSelectedValues([]);
      onChange([], []);
    } else {
      // Select all visible options
      const allValues = displayOptions.map(getOptionValue);
      setSelectedValues(allValues);

      // Update cache
      const newCache = new Map(selectedOptionsCache);
      displayOptions.forEach((option) => {
        const values = getOptionValue(option);
        newCache.set(values, option);
      });
      setSelectedOptionsCache(newCache);

      onChange(allValues, displayOptions);
    }
  }, [
    selectedValues.length,
    displayOptions,
    onChange,
    getOptionValue,
    selectedOptionsCache,
  ]);

  const handleRemoveSelected = useCallback(
    (valueToRemove: string) => {
      const newValues = selectedValues.filter((val) => val !== valueToRemove);
      setSelectedValues(newValues);

      const selectedObjects = newValues
        .map((val) => {
          return (
            displayOptions.find((opt) => getOptionValue(opt) === val) ||
            selectedOptionsCache.get(val)
          );
        })
        .filter(Boolean) as T[];

      onChange(newValues, selectedObjects);
    },
    [
      selectedValues,
      onChange,
      displayOptions,
      getOptionValue,
      selectedOptionsCache,
    ],
  );

  const handleClearAll = useCallback(() => {
    setSelectedValues([]);
    onChange([], []);
  }, [onChange]);

  const handleOpenChange = useCallback((newOpen: boolean) => {
    setOpen(newOpen);
    if (!newOpen) {
      setSearchTerm("");
    }
  }, []);

  const getDisplayValueForSelected = useCallback(
    (val: string): string => {
      // First try to find in selected options (fetched data)
      const option = selectedOptions.find((opt) => getOptionValue(opt) === val);
      if (option) {
        return String(getDisplayValue(option));
      }

      // Then try cached options
      const cachedOption = selectedOptionsCache.get(val);
      if (cachedOption) {
        return String(getDisplayValue(cachedOption));
      }

      // Use defaultLabels if provided
      if (defaultLabels) {
        const defaultLabel = defaultLabels.find(
          (option) => option.value === val,
        );
        if (defaultLabel) {
          return String(defaultLabel.label);
        }
      }

      // Fall back to defaultLabel or the values itself
      return defaultLabel || val;
    },
    [
      selectedOptions,
      getOptionValue,
      getDisplayValue,
      selectedOptionsCache,
      defaultLabels,
      defaultLabel,
    ],
  );

  const triggerDisplayValue = useMemo(() => {
    if (selectedValues.length === 0) {
      return placeholder;
    }

    if (selectedValues.length <= maxSelectedDisplay) {
      return (
        <div className='flex flex-wrap gap-1.5'>
          {selectedValues.map((val) => {
            const displayValue = getDisplayValueForSelected(val);

            return (
              <Badge
                key={val}
                variant='outline'
                className={cn("text-xs flex items-center gap-1")}
              >
                {displayValue}
                {!disabled && (
                  <X
                    className='w-3 h-3 cursor-pointer hover:text-destructive'
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      handleRemoveSelected(val);
                    }}
                  />
                )}
              </Badge>
            );
          })}
        </div>
      );
    }

    return (
      <CustomToolTip
        toolTipContent={
          <div className='flex flex-col gap-1'>
            {selectedValues.map((val) => {
              const displayValue = getDisplayValueForSelected(val);

              return (
                <span key={val} className='text-xs'>
                  {displayValue}
                </span>
              );
            })}
          </div>
        }
        toolTipTrigger={
          <div className='flex items-center gap-2'>
            <span className='text-sm'>
              {selectedValues.length} {label.toLowerCase()} selected
            </span>
          </div>
        }
      />
    );
  }, [
    selectedValues,
    maxSelectedDisplay,
    placeholder,
    getDisplayValueForSelected,
    disabled,
    handleRemoveSelected,
    label,
  ]);

  const loadingStates = useMemo(() => {
    const isFirstLoad = paginationState.offset === 0;
    const hasSearchQuery = searchTerm.trim().length > 0;

    return {
      showSearchLoader: hasSearchQuery && isFetching && !isLoadingMore,
      showMainSkeleton:
        (isLoading || (isFetching && isFirstLoad && !isLoadingMore)) &&
        !preload &&
        !searchTerm,
      showLoadMoreIndicator:
        isFetching && isLoadingMore && hasMore && displayOptions.length > 0,
    };
  }, [
    paginationState.offset,
    searchTerm,
    isFetching,
    isLoading,
    isLoadingMore,
    hasMore,
    displayOptions.length,
    preload,
  ]);

  return (
    <Popover open={open} onOpenChange={handleOpenChange} modal={true}>
      <PopoverTriggerButton
        role='combobox'
        className={cn(
          "justify-between min-h-10 h-auto bg-inherit hover:bg-inherit active:scale-100 ",
          disabled && "opacity-50 cursor-not-allowed",
          triggerClassName,
          placeholder === triggerDisplayValue && "text-muted-foreground/35",
          // selectedOptions.length === 0 && "text-muted-foreground/35",
        )}
        disabled={disabled}
        isArrowVisible={selectedOptions.length === 0}
        side={side}
        customIcon={
          selectedOptions.length > 0 &&
          clearable &&
          !disabled && (
            <X
              className='w-4 h-4 ml-2 opacity-50'
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                handleClearAll();
              }}
            />
          )
        }
      >
        {triggerDisplayValue}
      </PopoverTriggerButton>
      <PopoverContent
        className={cn(
          "p-2 popover-content-width-full border border-input rounded-[0.5rem] shadow-select min-w-fit",
          className,
        )}
        align='start'
        portal={false}
        side={side}
      >
        <Command shouldFilter={false}>
          <div className='relative w-full border-b'>
            <CommandInput
              placeholder={`Search ${label.toLowerCase()}...`}
              value={searchTerm}
              onValueChange={setSearchTerm}
            />
            {loadingStates.showSearchLoader && (
              <div className='absolute flex items-center transform -translate-y-1/2 right-2 top-1/2'>
                <Loader2 className='w-4 h-4 animate-spin text-muted-foreground' />
              </div>
            )}
          </div>
          <CommandList
            ref={scrollContainerRef}
            onScroll={handleScroll}
            className='overflow-y-scroll max-h-60'
          >
            {error && (
              <div className='p-4 text-center text-destructive'>
                Failed to fetch options
              </div>
            )}

            {loadingStates.showMainSkeleton &&
              (loadingSkeleton || <PaginatedLoader />)}

            {!isLoading &&
              !error &&
              displayOptions.length === 0 &&
              !loadingStates.showMainSkeleton &&
              (notFound || (
                <CommandEmpty>
                  {noResultsMessage ?? `No ${label.toLowerCase()} found.`}
                </CommandEmpty>
              ))}

            {displayOptions.length > 0 && (
              <CommandGroup>
                {allowSelectAll && (
                  <CommandItem
                    onSelect={handleSelectAll}
                    className='mb-1 font-medium border-b'
                  >
                    <div className='flex items-center gap-2'>
                      <div className='flex items-center justify-center w-4 h-4 border rounded-sm'>
                        {selectedValues.length === displayOptions.length && (
                          <Check className='w-3 h-3' />
                        )}
                        {selectedValues.length > 0 &&
                          selectedValues.length < displayOptions.length && (
                            <div className='w-2 h-2 bg-current rounded-sm' />
                          )}
                      </div>
                      Select All ({displayOptions.length})
                    </div>
                  </CommandItem>
                )}

                {displayOptions.map((option) => {
                  const optionValue = getOptionValue(option);
                  const isSelected = selectedValues.includes(optionValue);

                  return (
                    <CommandItem
                      key={optionValue}
                      value={optionValue}
                      onSelect={handleSelect}
                      className='truncate line-clamp-2 '
                    >
                      <div className='flex items-center w-full gap-2'>
                        <div className='flex items-center justify-center w-4 h-4 border rounded-sm'>
                          {isSelected && <Check className='w-3 h-3' />}
                        </div>
                        {renderOption(option)}
                      </div>
                    </CommandItem>
                  );
                })}
              </CommandGroup>
            )}

            {loadingStates.showLoadMoreIndicator && (
              <div className='p-3 text-center border-t bg-muted/5'>
                <div className='flex items-center justify-center gap-2'>
                  <Loader2 className='w-4 h-4 animate-spin text-muted-foreground' />
                  <p className='text-sm text-muted-foreground'>
                    Loading more...
                  </p>
                </div>
              </div>
            )}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
};
